import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import utc from 'dayjs/plugin/utc';
import { GymAccessCategoryType } from '@gym-particles/types/AccessCategoryMapping';
import { GymChainAccessCategories, GymAccessCategoriesResponse } from '@gym-src/API';
import { getAccessCategories } from '@gym-graphql/queries';
import { removeSiteLocationAccessCategory } from '@gym-graphql/mutations';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const initialState: {
  isAccessCategoriesLoading: boolean;
  items: Array<{
    siteLocationId: number;
    items: GymAccessCategoryType[];
    totalRecords: number;
  }>;
} = {
  isAccessCategoriesLoading: false,
  items: []
};

type accessCategoryParamType = {
  siteLocationId: number;
};

type accessCategoryDeleteParamType = {
  ids: number[];
  siteLocationId: number;
};

export const fetchAccessCategories = createAsyncThunk(
  '/gym/fetchAccessCategories',
  async (params: accessCategoryParamType) => {
    const response = await (API.graphql({
      query: getAccessCategories,
      variables: {
        siteLocationId: params.siteLocationId
      }
    }) as Promise<{
      data: {
        getAccessCategories: GymAccessCategoriesResponse;
      };
    }>);
    console.log(response);
    return response;
  }
);

export const deleteAccessCategory = createAsyncThunk(
  'AccessCategory/deleteAccessCategory',
  async (params: accessCategoryDeleteParamType) => {
    console.log(params.ids);
    const response = await (API.graphql({
      query: removeSiteLocationAccessCategory,
      variables: {
        ids: params.ids,
        siteLocationId: params.siteLocationId
      }
    }) as Promise<{
      data: {
        removeSiteLocationAccessCategory: number;
      };
    }>);
    return response;
  }
);

export const AccessCategoriesSlice = createSlice({
  name: 'AccessCategories',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAccessCategories.fulfilled, (state, action) => {
      state.isAccessCategoriesLoading = false;
      if (action.payload.data.getAccessCategories.items === null) {
        return;
      }
      console.log(action.payload.data.getAccessCategories.items);
      const items = action.payload.data.getAccessCategories.items.map(
        (e: GymChainAccessCategories): GymAccessCategoryType => {
          return {
            id: e.id || 0,
            siteId: e.siteId || 0,
            name: e.name || '',
            gender: e.gender || '',
            age: e.age || 0,
            schedulesOnly: e.schedulesOnly ? 'true' : 'false',
            status: e.status || '',
            siteLocationAccessCategoryId: e.siteLocationAccessCategoryId || 0,
            siteLocationId: e.siteLocationId || 0,
            siteLocationMembershipId: e.siteLocationMembershipId || 0,
            accessCategoryId: e.accessCategoryId || 0,
            locationName: e.locationName || '',
            arxAccessCategoryId: e.arxAccessCategoryId || '',
            importedId: e.importedId || '',
            membershipId: e.membershipId || 0,
            membershipName: e.membershipName || '',
            program: e.program || '',
            scheduleType: e.scheduleType || '',
            createdDate: e.createdDate ? dayjs(e.createdDate || '').format('DD MMM YYYY') : '',
            createdBy: e.createdBy || 0,
            lastModifiedBy: e.lastModifiedBy || 0,
            lastModifiedDate: e.lastModifiedDate || ''
          };
        }
      );

      const totalRecords = action.payload.data.getAccessCategories.totalRecords.totalRecords;

      const fetchedAccessCategoriesInfo = {
        totalRecords: totalRecords,
        items: items,
        siteLocationId: action.meta.arg.siteLocationId
      };

      if (state.items.length === 0) {
        state.items.push(fetchedAccessCategoriesInfo);
      } else {
        const update = state.items.find(
          (accessCategoryInfo) =>
            accessCategoryInfo.siteLocationId === fetchedAccessCategoriesInfo.siteLocationId
        );
        if (update) {
          const newState = state.items.map((accessCategoryInfo) => {
            if (accessCategoryInfo.siteLocationId === fetchedAccessCategoriesInfo.siteLocationId) {
              return fetchedAccessCategoriesInfo;
            } else {
              return accessCategoryInfo;
            }
          });
          state.items = newState;
        } else {
          state.items.push(fetchedAccessCategoriesInfo);
        }
      }
    });

    builder.addCase(fetchAccessCategories.pending, (state, action) => {
      state.isAccessCategoriesLoading = true;
      return state;
    });

    builder.addCase(fetchAccessCategories.rejected, (state, action) => {
      state.isAccessCategoriesLoading = false;
      console.log('getAccessCategories', action);
      return state;
    });

    builder.addCase(deleteAccessCategory.fulfilled, (state, action) => {
      state.isAccessCategoriesLoading = false;
      const siteLocationAccessCategoryIds = action.meta.arg.ids;
      const filtered = state.items.map((accessCategoryObj) => {
        const filteredItems = accessCategoryObj.items.filter(
          (accessCategory) =>
            !siteLocationAccessCategoryIds.includes(accessCategory.siteLocationAccessCategoryId)
        );

        return {
          ...accessCategoryObj,
          items: filteredItems,
          totalRecords: filteredItems.length
        };
      });
      state.items = filtered;
    });

    builder.addCase(deleteAccessCategory.pending, (state, action) => {
      state.isAccessCategoriesLoading = true;
      return state;
    });

    builder.addCase(deleteAccessCategory.rejected, (state, action) => {
      state.isAccessCategoriesLoading = false;
      console.error('deleteAccessCategory', action);
      return state;
    });
  }
});

export default AccessCategoriesSlice.reducer;
